import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../../../../API';
import StagedItemsBrowser from '../../fileUpload/components/StagedItemsBrowser';
import { parseFilenameInput } from './parseFilenameList';
import styles from './slides.module.css';
import enhancedStyles from '../CaseBuilderEnhanced.module.css';

const TAB_STAGING = 'staging';
const TAB_LIST = 'list';
const TAB_LOCAL = 'local';

function countSelection(payload) {
    const items = payload?.items?.length || 0;
    const folderItems = (payload?.folders || []).reduce((n, folder) => n + (folder.items?.length || 0), 0);
    return items + folderItems;
}

export default function AddSlidesDialog({
    open,
    onClose,
    acceptAndSaveStagedItems,
    acceptStagedLoading = false,
    uploadLocalFiles,
    uploading = false,
    uploadProgress = {},
    onAfterAdd,
}) {
    const [tab, setTab] = useState(TAB_STAGING);
    const [stagingSelection, setStagingSelection] = useState({ items: [], folders: [] });
    const [listText, setListText] = useState('');
    const [csvText, setCsvText] = useState('');
    const [lookupResult, setLookupResult] = useState(null);
    const [lookupPending, setLookupPending] = useState(false);
    const [lookupError, setLookupError] = useState(null);
    const [resolvedAmbiguous, setResolvedAmbiguous] = useState({});
    const [skippedQueries, setSkippedQueries] = useState(() => new Set());
    const [localFiles, setLocalFiles] = useState([]);
    const fileInputRef = useRef(null);
    const csvInputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setTab(TAB_STAGING);
            setStagingSelection({ items: [], folders: [] });
            setListText('');
            setCsvText('');
            setLookupResult(null);
            setLookupError(null);
            setResolvedAmbiguous({});
            setSkippedQueries(new Set());
            setLocalFiles([]);
        }
    }, [open]);

    const parsedNames = useMemo(
        () => parseFilenameInput({ text: listText, csvText }).names,
        [listText, csvText],
    );

    const handleCsvFile = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setCsvText(String(reader.result || ''));
        };
        reader.readAsText(file);
    };

    const runLookup = async () => {
        if (!parsedNames.length) return;
        setLookupPending(true);
        setLookupError(null);
        try {
            const response = await api.post('/network/staging/items/lookup', {
                body: { names: parsedNames },
            });
            if (response.status !== 200) {
                throw new Error(response.statusText || 'Lookup failed');
            }
            setLookupResult(response.data || { matches: [], unmatched: [], ambiguous: [] });
            setResolvedAmbiguous({});
            setSkippedQueries(new Set());
        } catch (err) {
            setLookupError(err?.message || 'Lookup failed');
            setLookupResult(null);
        } finally {
            setLookupPending(false);
        }
    };

    const listItemsToAccept = useMemo(() => {
        if (!lookupResult) return [];
        const items = (lookupResult.matches || []).map((row) => row.item).filter(Boolean);
        Object.entries(resolvedAmbiguous).forEach(([query, item]) => {
            if (item && !skippedQueries.has(query)) items.push(item);
        });
        return items;
    }, [lookupResult, resolvedAmbiguous, skippedQueries]);

    const unresolvedAmbiguous = useMemo(() => {
        if (!lookupResult) return [];
        return (lookupResult.ambiguous || []).filter((row) => {
            if (skippedQueries.has(row.query)) return false;
            return !resolvedAmbiguous[row.query];
        });
    }, [lookupResult, resolvedAmbiguous, skippedQueries]);

    const unmatchedVisible = useMemo(() => {
        if (!lookupResult) return [];
        return (lookupResult.unmatched || []).filter((name) => !skippedQueries.has(name));
    }, [lookupResult, skippedQueries]);

    const stagingCount = countSelection(stagingSelection);

    const primaryDisabled = (() => {
        if (acceptStagedLoading || uploading) return true;
        if (tab === TAB_STAGING) return stagingCount === 0;
        if (tab === TAB_LIST) {
            return listItemsToAccept.length === 0 || unresolvedAmbiguous.length > 0;
        }
        if (tab === TAB_LOCAL) return localFiles.length === 0;
        return true;
    })();

    const primaryLabel = (() => {
        if (acceptStagedLoading || uploading) return 'Adding…';
        if (tab === TAB_LOCAL) {
            return localFiles.length ? `Upload ${localFiles.length}` : 'Upload';
        }
        if (tab === TAB_LIST) {
            return listItemsToAccept.length ? `Add selected (${listItemsToAccept.length})` : 'Add selected';
        }
        return stagingCount ? `Add selected (${stagingCount})` : 'Add selected';
    })();

    const handlePrimary = async () => {
        try {
            let caseDoc = null;
            if (tab === TAB_STAGING) {
                caseDoc = await acceptAndSaveStagedItems(stagingSelection);
            } else if (tab === TAB_LIST) {
                caseDoc = await acceptAndSaveStagedItems({ items: listItemsToAccept, folders: [] });
            } else if (tab === TAB_LOCAL) {
                caseDoc = await uploadLocalFiles(localFiles);
            }
            onAfterAdd?.(caseDoc);
            onClose();
        } catch (_) {
            // errors already surfaced by hooks
        }
    };

    const handleLocalSelect = (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (!files.length) return;
        setLocalFiles((prev) => {
            const next = [...prev];
            files.forEach((file) => {
                if (!next.some((existing) => existing.name === file.name && existing.size === file.size)) {
                    next.push(file);
                }
            });
            return next;
        });
    };

    const onStagingSelectionChange = useCallback((payload) => {
        setStagingSelection(payload || { items: [], folders: [] });
    }, []);

    if (!open) return null;

    return (
        <div className={styles.overlay} onClick={onClose} role="presentation">
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="add-slides-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.dialogHeader}>
                    <div>
                        <h3 id="add-slides-title" className={styles.dialogTitle}>Add slides</h3>
                        <p className={styles.dialogSubtitle}>
                            Staging, filename list, or this computer — then assign block and stain on the case.
                        </p>
                    </div>
                    <button type="button" className={enhancedStyles.linkButton} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className={styles.tabs} role="tablist" aria-label="Add slides method">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === TAB_STAGING}
                        className={`${styles.tab} ${tab === TAB_STAGING ? styles.tabActive : ''}`}
                        onClick={() => setTab(TAB_STAGING)}
                    >
                        Staging
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === TAB_LIST}
                        className={`${styles.tab} ${tab === TAB_LIST ? styles.tabActive : ''}`}
                        onClick={() => setTab(TAB_LIST)}
                    >
                        Filename list / CSV
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === TAB_LOCAL}
                        className={`${styles.tab} ${tab === TAB_LOCAL ? styles.tabActive : ''}`}
                        onClick={() => setTab(TAB_LOCAL)}
                    >
                        This computer
                    </button>
                </div>

                <div className={styles.dialogBody}>
                    <div hidden={tab !== TAB_STAGING}>
                        <StagedItemsBrowser
                            isOpen={open}
                            embedded
                            hideFooter
                            onClose={onClose}
                            onAccept={acceptAndSaveStagedItems}
                            acceptLoading={acceptStagedLoading}
                            onSelectionChange={onStagingSelectionChange}
                        />
                    </div>

                    {tab === TAB_LIST && (
                        <>
                            <p className={styles.helpText}>
                                Paste one filename per line, or upload a CSV (first column). Names are matched against
                                staging (basename, case-insensitive). Resolve ambiguous matches before adding.
                            </p>
                            <textarea
                                className={styles.textarea}
                                value={listText}
                                onChange={(e) => setListText(e.target.value)}
                                placeholder={'slide1.svs\nslide2.svs'}
                                aria-label="Filename list"
                            />
                            <div className={styles.fileInputRow}>
                                <input
                                    ref={csvInputRef}
                                    type="file"
                                    accept=".csv,text/csv,text/plain"
                                    onChange={handleCsvFile}
                                    style={{ display: 'none' }}
                                />
                                <button
                                    type="button"
                                    className={enhancedStyles.buttonSecondary}
                                    onClick={() => csvInputRef.current?.click()}
                                >
                                    Upload CSV
                                </button>
                                {csvText ? (
                                    <span className={styles.progress}>CSV loaded ({parseFilenameInput({ csvText }).names.length} names)</span>
                                ) : null}
                                <button
                                    type="button"
                                    className={enhancedStyles.buttonPrimary}
                                    disabled={!parsedNames.length || lookupPending}
                                    onClick={runLookup}
                                >
                                    {lookupPending ? 'Looking up…' : `Look up (${parsedNames.length})`}
                                </button>
                            </div>
                            {lookupError && <p className={styles.errorText}>{lookupError}</p>}
                            {lookupResult && (
                                <>
                                    <p className={styles.lookupStatus}>
                                        {lookupResult.matches?.length || 0} matched · {unmatchedVisible.length} unmatched · {lookupResult.ambiguous?.length || 0} ambiguous
                                    </p>
                                    {unmatchedVisible.length > 0 && (
                                        <div>
                                            <h4 className={styles.sectionTitle}>Unmatched</h4>
                                            <ul className={styles.lookupList}>
                                                {unmatchedVisible.map((name) => (
                                                    <li key={name}>
                                                        {name}{' '}
                                                        <button
                                                            type="button"
                                                            className={enhancedStyles.linkButton}
                                                            onClick={() => setSkippedQueries((prev) => new Set(prev).add(name))}
                                                        >
                                                            Skip
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {(lookupResult.ambiguous || []).map((row) => (
                                        <div key={row.query}>
                                            <h4 className={styles.sectionTitle}>Ambiguous: {row.query}</h4>
                                            {skippedQueries.has(row.query) ? (
                                                <p className={styles.helpText}>Skipped</p>
                                            ) : (
                                                <div className={styles.ambiguousChoices}>
                                                    {(row.items || []).map((item) => (
                                                        <label key={item._id}>
                                                            <input
                                                                type="radio"
                                                                name={`amb-${row.query}`}
                                                                checked={resolvedAmbiguous[row.query]?._id === item._id}
                                                                onChange={() => setResolvedAmbiguous((prev) => ({ ...prev, [row.query]: item }))}
                                                            />{' '}
                                                            {item.name} ({item._id})
                                                        </label>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className={enhancedStyles.linkButton}
                                                        onClick={() => setSkippedQueries((prev) => new Set(prev).add(row.query))}
                                                    >
                                                        Skip this name
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {lookupResult.matches?.length > 0 && (
                                        <div>
                                            <h4 className={styles.sectionTitle}>Matched</h4>
                                            <ul className={styles.lookupList}>
                                                {lookupResult.matches.map((row) => (
                                                    <li key={`${row.query}-${row.item?._id}`}>{row.query} → {row.item?.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {tab === TAB_LOCAL && (
                        <>
                            <p className={styles.helpText}>
                                Files upload immediately into this case, quarantined. Assign block and stain after they appear in the list.
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleLocalSelect}
                                style={{ display: 'none' }}
                            />
                            <button
                                type="button"
                                className={enhancedStyles.buttonSecondary}
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                            >
                                Choose files
                            </button>
                            {localFiles.length > 0 && (
                                <ul className={styles.localFileList}>
                                    {localFiles.map((file) => (
                                        <li key={`${file.name}-${file.size}`}>
                                            <span>{file.name}</span>
                                            <span className={styles.progress}>
                                                {uploadProgress[file.name]?.status === 'uploading'
                                                    ? `${uploadProgress[file.name].progress || 0}%`
                                                    : `${Math.max(1, Math.round((file.size || 0) / 1024 / 1024))} MB`}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>

                <div className={styles.dialogFooter}>
                    <button type="button" className={enhancedStyles.buttonSecondary} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={enhancedStyles.buttonPrimary}
                        disabled={primaryDisabled}
                        onClick={handlePrimary}
                    >
                        {primaryLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
